import { useEffect, useState } from 'react';
import { Alert, Button, message } from 'antd';
import CreateJobModal from '@components/CreateJobModal';
import { getJobModelReleaseStatus, type JobItem, type JobModelReleaseStatus } from '@/apis/simulation';

interface ModelReleaseStaleAlertProps {
  jobId: number;
  /** 当前 Job 的列表项；用作「按最新主版本新建」的复制来源。 */
  jobMeta: JobItem | null;
}

/**
 * 模型主版本在 Job 接受时就冻结成清单。之后给主版本追加/替换模型，该 Job 和它的 task 重试
 * 都不会用到新内容；这里检测到不一致时提示用户，并一键复制该 Job 新建（新建时重新解析主版本）。
 */
export default function ModelReleaseStaleAlert({ jobId, jobMeta }: ModelReleaseStaleAlertProps) {
  const [status, setStatus] = useState<JobModelReleaseStatus | null>(null);
  const [copyOpen, setCopyOpen] = useState(false);
  const releaseName = jobMeta?.customParams?.model_release;

  useEffect(() => {
    setStatus(null);
    if (!jobId || typeof releaseName !== 'string' || !releaseName) return;
    let cancelled = false;
    getJobModelReleaseStatus(jobId)
      .then((result) => { if (!cancelled) setStatus(result); })
      .catch((err) => console.error('查询模型主版本状态失败:', err));
    return () => { cancelled = true; };
  }, [jobId, releaseName]);

  if (!status?.stale || !status.modelRelease) return null;
  const description = status.currentError
    ? `主版本当前无法解析（${status.currentError}）。本 Job 及其重试仍使用接受时冻结的清单。`
    : '该 Job 接受后，主版本又追加或替换了模型。本 Job 及其 task 重试仍使用接受时冻结的清单，'
      + '要使用最新内容请按最新主版本新建 Job。';
  return (
    <>
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message={`模型主版本 ${status.modelRelease} 已更新，与本 Job 冻结的清单不一致`}
        description={description}
        action={!status.currentError && jobMeta ? (
          <Button size="small" onClick={() => setCopyOpen(true)}>按最新主版本新建</Button>
        ) : undefined}
      />
      {copyOpen && (
        <CreateJobModal
          open={copyOpen}
          copyRecord={jobMeta}
          onClose={() => setCopyOpen(false)}
          onSuccess={() => { setCopyOpen(false); message.success('任务已创建'); }}
        />
      )}
    </>
  );
}
